import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box,
  FormHelperText,
} from "@mui/material";
import FileUpload from "../../GovernanceDashboard/DataEntry/FileUpload";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, put } from "../../../utils/api";
import useAuthStore from "../../../store/ContextUser";
import { textError } from "../../../utils/validationMessage";
import {
  annualPolicyYesFieldErrors,
  getEffectiveDateMinMax,
  hasUploadedDocument,
  isAnnualPolicyUploadDisabled,
  shouldShowAnnualPolicyRevised,
} from "../../../utils/annualPolicyFormHelpers";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import DeleteIcon from "@mui/icons-material/Delete";
import WestIcon from "@mui/icons-material/West";
import { Troubleshoot } from "@mui/icons-material";

const Diversity = () => {
  const SOCIAL_PRIMARY = "#1C486B";
  const socialRadioSx = {
    color: SOCIAL_PRIMARY,
    "&.Mui-checked": { color: SOCIAL_PRIMARY },
  };
  const backButtonSx = {
    color: SOCIAL_PRIMARY,
    textTransform: "none",
    border: `1px solid ${SOCIAL_PRIMARY}`,
    "&:hover": { borderColor: SOCIAL_PRIMARY },
  };
  const primaryButtonSx = {
    textTransform: "none",
    backgroundColor: SOCIAL_PRIMARY,
    "&:hover": { backgroundColor: "#163a56" },
  };
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedCompany } = useAuthStore();
  const [errors, setErrors] = useState({});
  const [module, setModule] = useState("monthly");
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState({ fieldName: "", index: "" });
  const [deletedPolicyDocs, setDeletedPolicyDocs] = useState({
    equalOpportunityPolicy: false,
    genderBasedViolencePolicy: false,
  });
  const [hasExistingData, setHasExistingData] = useState(false);
  const [existingEqualOpportunityDocUrl, setExistingEqualOpportunityDocUrl] =
    useState("");
  const [
    existingGenderBasedViolenceDocUrl,
    setExistingGenderBasedViolenceDocUrl,
  ] = useState("");
  const [prevEqualOpportunity, setPrevEqualOpportunity] = useState({
    documentUrl: "",
    remark: "",
    effectiveDate: "",
  });
  const [prevGenderBasedViolence, setPrevGenderBasedViolence] = useState({
    documentUrl: "",
    remark: "",
    effectiveDate: "",
  });
  const [isFirstEntry, setIsFirstEntry] = useState(false);
  const [previousYearEqualOpportunityDocUrl, setPreviousYearEqualOpportunityDocUrl] =
    useState("");
  const [previousYearGenderViolenceDocUrl, setPreviousYearGenderViolenceDocUrl] =
    useState("");
  const [entryYear, setEntryYear] = useState(null);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const removeCommas = (val) => {
    if (val === null || val === undefined) return "";
    return val.toString().replace(/,/g, "").trim();
  };
  const parseNumber = (val) => {
    if (!val || val === "" || val === null || val === undefined) return 0;
    const parsed = parseFloat(removeCommas(val));
    return isNaN(parsed) ? 0 : parsed;
  };
  const formatWithCommas = (val) => {
    const normalized = removeCommas(val);
    if (normalized === "") return "";
    if (!/^\d*\.?\d*$/.test(normalized)) return normalized;
    const [integerPart = "", decimalPart] = normalized.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };
  const formatNumericValuesDeep = (value) => {
    if (Array.isArray(value)) return value.map(formatNumericValuesDeep);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, formatNumericValuesDeep(v)]),
      );
    }
    if (typeof value === "string") {
      const normalized = removeCommas(value);
      return normalized !== "" && /^\d*\.?\d*$/.test(normalized)
        ? formatWithCommas(normalized)
        : value;
    }
    if (typeof value === "number") return formatWithCommas(value);
    return value;
  };
  const stripCommaNumericValuesDeep = (value) => {
    if (Array.isArray(value)) return value.map(stripCommaNumericValuesDeep);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, stripCommaNumericValuesDeep(v)]),
      );
    }
    if (typeof value === "string") {
      const normalized = removeCommas(value);
      return normalized !== "" && /^\d*\.?\d*$/.test(normalized)
        ? normalized
        : value;
    }
    return value;
  };
  const todayIso = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear();
  const maxEffectiveDate =
    Number(entryYear) && Number(entryYear) < currentYear
      ? `${entryYear}-12-31`
      : todayIso;
  const { min: diversityPolicyEffMin, max: diversityPolicyEffMax } =
    getEffectiveDateMinMax(entryYear);

  // Monthly formData
  const monthlyFormDataInitial = {
    genderDiversity: {
      maleNos: "",
      malePercentage: "",
      femaleNos: "",
      femalePercentage: "",
    },
    ethnicDiversity: [
      {
        groupName: "",
        diversityNos: "",
        diversityPercentage: "",
      },
    ],
    diversityInLeadership: {
      maleNos: "",
      malePercentage: "",
      femaleNos: "",
      femalePercentage: "",
      othersNos: "",
      othersPercentage: "",
      totalNos: "",
      totalPercentage: "",
    },
  };

  // Annual formData
  const annualFormDataInitial = {
    genderDiversity: {
      male: "",
      female: "",
      others: "",
    },
    ethnicDiversity: [{ groupName: "", diversityPercentage: "" }],
    employeesWithDisabilities: "",
    diversityInLeadership: {
      gender: [
        { name: "Male", diversityPercentage: "" },
        { name: "Female", diversityPercentage: "" },
        { name: "Others", diversityPercentage: "" },
      ],
      ethnicity: [{ groupName: "", diversityPercentage: "" }],
    },
    equalOpportunityPolicy: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
    genderBasedViolencePolicy: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
  };

  const [monthlyFormData, setMonthlyFormData] = useState(
    monthlyFormDataInitial,
  );
  const [annualFormData, setAnnualFormData] = useState(annualFormDataInitial);

  // Get the appropriate formData based on module
  const formData = module === "annual" ? annualFormData : monthlyFormData;
  const setFormData =
    module === "annual" ? setAnnualFormData : setMonthlyFormData;

  const [isLoading, setIsLoading] = useState(true);

  const getEthnicDiversityFieldError = (index, field) => {
    const ethnicErrors = errors?.ethnicDiversity;
    if (!ethnicErrors) return "";
    if (Array.isArray(ethnicErrors)) {
      return ethnicErrors?.[index]?.[field] || "";
    }
    if (typeof ethnicErrors === "object") {
      if (ethnicErrors?.[index] && typeof ethnicErrors[index] === "object") {
        return ethnicErrors[index]?.[field] || "";
      }
      return ethnicErrors?.[field] || "";
    }
    return "";
  };

  // For monthly ethnic diversity, compute total of all diversityNos
  const totalEthnicDiversityNos =
    module === "monthly" && Array.isArray(formData.ethnicDiversity)
      ? formData.ethnicDiversity.reduce((sum, item) => {
          const value = parseNumber(item?.diversityNos);
          return sum + (isNaN(value) ? 0 : value);
        }, 0)
      : 0;

  const fetchData = useCallback(
    async (id) => {
      try {
        const response = await get(`/social/getSocial?id=${id}`);
        // Handle array response - get first item if array, otherwise use directly
        const responseData = response?.data?.data;
        const socialData = Array.isArray(responseData)
          ? responseData[0]
          : responseData;
        const fetchedData = socialData?.diversityAndInclusion || {};
        const employeeWellBeing = socialData?.employeeWellBeing || {};

        // Get module from URL params, API response, or default to monthly
        const urlModule = searchParams.get("entryType");
        const apiModule =
          socialData?.module || socialData?._socialDataRef?.module;
        let finalModule = "monthly";

        console.log(apiModule, response, "apiModule");

        if (
          urlModule === "annual" ||
          apiModule === "Annual" ||
          apiModule === "annual"
        ) {
          finalModule = "annual";
        } else if (
          urlModule === "monthly" ||
          apiModule === "Monthly" ||
          apiModule === "monthly"
        ) {
          finalModule = "monthly";
        }

        console.log(finalModule, urlModule, apiModule, "finalModule+++");

        setModule(finalModule);

        console.log(fetchedData, "values of the FetchData");

        // Calculate Male (No's) from employeeWellBeing for monthly module
        const parseNumber = (val) => {
          if (!val || val === "" || val === null || val === undefined) return 0;
          const parsed = parseFloat(removeCommas(val));
          return isNaN(parsed) ? 0 : parsed;
        };
        const sanitizeDocumentUrl = (value) => {
          if (typeof value !== "string") return value || "";
          const cleaned = value.trim();
          if (!cleaned) return "";
          if (cleaned.toLowerCase() === "no documents available") return "";
          return cleaned;
        };

        const calculatedMaleNos =
          parseNumber(employeeWellBeing.permEmpDifferentlyAbledMale) +
          parseNumber(employeeWellBeing.permEmpMale) +
          parseNumber(employeeWellBeing.tempEmpDifferentlyAbledMale) +
          parseNumber(employeeWellBeing.tempEmpMale) +
          parseNumber(employeeWellBeing.permWorkerDifferentlyAbledMale) +
          parseNumber(employeeWellBeing.permWorkerMale) +
          parseNumber(employeeWellBeing.tempWorkerDifferentlyAbledMale) +
          parseNumber(employeeWellBeing.tempWorkerMale);

        const calculatedFemaleNos =
          parseNumber(employeeWellBeing.permEmpDifferentlyAbledFemale) +
          parseNumber(employeeWellBeing.permEmpFemale) +
          parseNumber(employeeWellBeing.tempEmpDifferentlyAbledFemale) +
          parseNumber(employeeWellBeing.tempEmpFemale) +
          parseNumber(employeeWellBeing.permWorkerDifferentlyAbledFemale) +
          parseNumber(employeeWellBeing.permWorkerFemale) +
          parseNumber(employeeWellBeing.tempWorkerDifferentlyAbledFemale) +
          parseNumber(employeeWellBeing.tempWorkerFemale);

        // Calculate percentages: (maleNos / totalEmployees) * 100
        const calculatedTotalEmployees = parseNumber(
          employeeWellBeing.totalEmployees,
        );
        setTotalEmployees(calculatedTotalEmployees);
        const calculatedMalePercentage =
          calculatedTotalEmployees > 0
            ? ((calculatedMaleNos / calculatedTotalEmployees) * 100).toFixed(2)
            : "";
        const calculatedFemalePercentage =
          calculatedTotalEmployees > 0
            ? ((calculatedFemaleNos / calculatedTotalEmployees) * 100).toFixed(
                2,
              )
            : "";

        // Separate monthly and annual data
        const monthlyData = {};
        const annualData = {};

        // Since diversity data structure is the same for both, we'll populate both
        // but they can be customized if needed
        // Resolve current entry year to query previous evidence alongside
        const currentEntryYear =
          Number(socialData?._socialDataRef?.yearToSubmit) ||
          Number(socialData?.yearToSubmit) ||
          Number(socialData?.year) ||
          undefined;
        setEntryYear(currentEntryYear || null);

        // Try to fetch previous annual data to prefill evidence if available
        let prevPolicyDocUrl = "";
        let prevPolicyEffectiveDate = "";
        let prevPolicyRemark = "";
        let prevPolicyStatusRemark = "";
        let prevPolicyStatus;
        let prevPolicyIsRevised;
        let prevGenderPolicyDocUrl = "";
        let prevGenderPolicyRemark = "";
        let prevGenderPolicyStatusRemark = "";
        let prevGenderPolicyEffectiveDate = "";
        let prevGenderPolicyStatus;
        let prevGenderPolicyIsRevised;
        try {
          const qs = new URLSearchParams();
          if (currentEntryYear) qs.set("year", String(currentEntryYear));
          if (selectedCompany?._id)
            qs.set("companyId", String(selectedCompany._id));
          const prevUrl = `/social/totalWorkforce/annualRangeWithPrev${qs.toString() ? `?${qs.toString()}` : ""}`;
          const prevRes = await get(prevUrl);
          setIsFirstEntry(Boolean(prevRes?.data?.data?.isFirstEntry));
          // Read previous policy data from both legacy and new API shapes
          const prevPolicies = prevRes?.data?.data?.policies || {};
          const prevEqualFromPolicies =
            prevPolicies?.["diversityAndInclusion.equalOpportunityPolicy"] || {};
          const prevGenderFromPolicies =
            prevPolicies?.["diversityAndInclusion.genderBasedViolencePolicy"] ||
            {};

          // Try a few common shapes to locate previous Equal Opportunity policy evidence
          prevPolicyDocUrl = sanitizeDocumentUrl(
            prevEqualFromPolicies?.documentUrl ||
            prevRes?.data?.previous?.diversityAndInclusion
              ?.equalOpportunityPolicy?.documentUrl ||
            prevRes?.data?.data?.previous?.diversityAndInclusion
              ?.equalOpportunityPolicy?.documentUrl ||
            (Array.isArray(prevRes?.data?.annual) &&
              prevRes.data.annual[0]?.diversityAndInclusion
                ?.equalOpportunityPolicy?.documentUrl) ||
            prevRes?.data?.data?.document?.documentUrl ||
            ""
          );
          prevPolicyEffectiveDate =
            prevEqualFromPolicies?.effectiveDate ||
            prevRes?.data?.data?.document?.effectiveDate || "";
          prevPolicyRemark =
            prevEqualFromPolicies?.remark ||
            prevRes?.data?.data?.document?.remark ||
            "";
          prevPolicyStatusRemark = prevEqualFromPolicies?.statusremark || "";
          prevPolicyStatus = prevEqualFromPolicies?.status;
          prevPolicyIsRevised = prevEqualFromPolicies?.isRevised;
          prevGenderPolicyDocUrl = sanitizeDocumentUrl(
            prevGenderFromPolicies?.documentUrl ||
            prevRes?.data?.previous?.diversityAndInclusion
              ?.genderBasedViolencePolicy?.documentUrl ||
            prevRes?.data?.data?.previous?.diversityAndInclusion
              ?.genderBasedViolencePolicy?.documentUrl ||
            (Array.isArray(prevRes?.data?.annual) &&
              prevRes.data.annual[0]?.diversityAndInclusion
                ?.genderBasedViolencePolicy?.documentUrl) ||
            ""
          );
          prevGenderPolicyRemark =
            prevGenderFromPolicies?.remark ||
            prevRes?.data?.previous?.diversityAndInclusion
              ?.genderBasedViolencePolicy?.remark ||
            prevRes?.data?.data?.previous?.diversityAndInclusion
              ?.genderBasedViolencePolicy?.remark ||
            (Array.isArray(prevRes?.data?.annual) &&
              prevRes.data.annual[0]?.diversityAndInclusion
                ?.genderBasedViolencePolicy?.remark) ||
            "";
          prevGenderPolicyStatusRemark =
            prevGenderFromPolicies?.statusremark || "";
          prevGenderPolicyEffectiveDate =
            prevGenderFromPolicies?.effectiveDate ||
            prevRes?.data?.previous?.diversityAndInclusion
              ?.genderBasedViolencePolicy?.effectiveDate ||
            prevRes?.data?.data?.previous?.diversityAndInclusion
              ?.genderBasedViolencePolicy?.effectiveDate ||
            (Array.isArray(prevRes?.data?.annual) &&
              prevRes.data.annual[0]?.diversityAndInclusion
                ?.genderBasedViolencePolicy?.effectiveDate) ||
            "";
          prevGenderPolicyStatus = prevGenderFromPolicies?.status;
          prevGenderPolicyIsRevised = prevGenderFromPolicies?.isRevised;
          setPreviousYearEqualOpportunityDocUrl(
            sanitizeDocumentUrl(prevEqualFromPolicies?.documentUrl || ""),
          );
          setPreviousYearGenderViolenceDocUrl(
            sanitizeDocumentUrl(prevGenderFromPolicies?.documentUrl || ""),
          );
        } catch (e) {
          // Non-blocking; ignore if endpoint not available
          setIsFirstEntry(false);
          setPreviousYearEqualOpportunityDocUrl("");
          setPreviousYearGenderViolenceDocUrl("");
        }

        if (fetchedData) {
          // Populate both monthly and annual with the same structure
          // You can customize this if monthly/annual have different fields
          Object.keys(fetchedData).forEach((key) => {
            monthlyData[key] = fetchedData[key];
            annualData[key] = fetchedData[key];
          });

          // Check if there's existing data for equalOpportunityPolicy
          const hasPolicyData =
            fetchedData?.equalOpportunityPolicy &&
            (fetchedData.equalOpportunityPolicy.status !== undefined ||
              fetchedData.equalOpportunityPolicy.isRevised !== undefined ||
              fetchedData.equalOpportunityPolicy.documentUrl ||
              fetchedData.equalOpportunityPolicy.effectiveDate);
          const initialPrev = {
            documentUrl:
              sanitizeDocumentUrl(
                fetchedData?.equalOpportunityPolicy?.documentUrl || ""
              ) ||
              prevPolicyDocUrl ||
              "",
            remark:
              fetchedData?.equalOpportunityPolicy?.remark ||
              prevPolicyRemark ||
              "",
            effectiveDate:
              fetchedData?.equalOpportunityPolicy?.effectiveDate ||
              prevPolicyEffectiveDate ||
              "",
          };
          const initialPrevGenderPolicy = {
            documentUrl:
              sanitizeDocumentUrl(
                fetchedData?.genderBasedViolencePolicy?.documentUrl || ""
              ) ||
              prevGenderPolicyDocUrl ||
              "",
            remark:
              fetchedData?.genderBasedViolencePolicy?.remark ||
              prevGenderPolicyRemark ||
              "",
            effectiveDate:
              fetchedData?.genderBasedViolencePolicy?.effectiveDate ||
              prevGenderPolicyEffectiveDate ||
              "",
          };
          setExistingEqualOpportunityDocUrl(initialPrev.documentUrl);
          setExistingGenderBasedViolenceDocUrl(
            initialPrevGenderPolicy.documentUrl,
          );
          setPrevEqualOpportunity(initialPrev);
          setPrevGenderBasedViolence(initialPrevGenderPolicy);
          setHasExistingData(!!hasPolicyData);
        } else {
          setHasExistingData(false);
        }

        // Update monthly form data (monthly has different structure)
        setMonthlyFormData((prevState) => formatNumericValuesDeep({
          genderDiversity: {
            maleNos:
              calculatedMaleNos > 0
                ? calculatedMaleNos.toString()
                : monthlyData?.genderDiversity?.maleNos === 0
                  ? 0
                  : monthlyData?.genderDiversity?.maleNos ||
                    prevState?.genderDiversity?.maleNos ||
                    "",
            malePercentage: calculatedMalePercentage
              ? calculatedMalePercentage
              : monthlyData?.genderDiversity?.malePercentage === 0
                ? 0
                : monthlyData?.genderDiversity?.malePercentage ||
                  prevState?.genderDiversity?.malePercentage ||
                  "",
            femaleNos:
              calculatedFemaleNos > 0
                ? calculatedFemaleNos.toString()
                : monthlyData?.genderDiversity?.femaleNos === 0
                  ? 0
                  : monthlyData?.genderDiversity?.femaleNos ||
                    prevState?.genderDiversity?.femaleNos ||
                    "",
            femalePercentage: calculatedFemalePercentage
              ? calculatedFemalePercentage
              : monthlyData?.genderDiversity?.femalePercentage === 0
                ? 0
                : monthlyData?.genderDiversity?.femalePercentage ||
                  prevState?.genderDiversity?.femalePercentage ||
                  "",
          },
          ethnicDiversity:
            monthlyData?.ethnicDiversity?.length > 0
              ? monthlyData.ethnicDiversity.map((item) => ({
                  groupName: item.groupName || "",
                  diversityNos: item.diversityNos || "",
                  diversityPercentage: item.diversityPercentage || "",
                }))
              : prevState.ethnicDiversity || [
                  { groupName: "", diversityNos: "", diversityPercentage: "" },
                ],
          diversityInLeadership: {
            maleNos:
              monthlyData?.diversityInLeadership?.maleNos === 0
                ? 0
                : monthlyData?.diversityInLeadership?.maleNos ||
                  prevState?.diversityInLeadership?.maleNos ||
                  "",
            malePercentage: (() => {
              const parseNumber = (val) => {
                if (!val || val === "" || val === null || val === undefined)
                  return 0;
                const parsed = parseFloat(removeCommas(val));
                return isNaN(parsed) ? 0 : parsed;
              };

              const maleNos = parseNumber(
                monthlyData?.diversityInLeadership?.maleNos ||
                  prevState?.diversityInLeadership?.maleNos ||
                  "",
              );
              const femaleNos = parseNumber(
                monthlyData?.diversityInLeadership?.femaleNos ||
                  prevState?.diversityInLeadership?.femaleNos ||
                  "",
              );
              const othersNos = parseNumber(
                monthlyData?.diversityInLeadership?.othersNos ||
                  prevState?.diversityInLeadership?.othersNos ||
                  "",
              );
              const totalNos = maleNos + femaleNos + othersNos;

              if (totalNos > 0) {
                return ((maleNos / totalNos) * 100).toFixed(2);
              }
              return monthlyData?.diversityInLeadership?.malePercentage === 0
                ? 0
                : monthlyData?.diversityInLeadership?.malePercentage ||
                    prevState?.diversityInLeadership?.malePercentage ||
                    "";
            })(),
            femaleNos:
              monthlyData?.diversityInLeadership?.femaleNos === 0
                ? 0
                : monthlyData?.diversityInLeadership?.femaleNos ||
                  prevState?.diversityInLeadership?.femaleNos ||
                  "",
            femalePercentage: (() => {
              const parseNumber = (val) => {
                if (!val || val === "" || val === null || val === undefined)
                  return 0;
                const parsed = parseFloat(removeCommas(val));
                return isNaN(parsed) ? 0 : parsed;
              };

              const maleNos = parseNumber(
                monthlyData?.diversityInLeadership?.maleNos ||
                  prevState?.diversityInLeadership?.maleNos ||
                  "",
              );
              const femaleNos = parseNumber(
                monthlyData?.diversityInLeadership?.femaleNos ||
                  prevState?.diversityInLeadership?.femaleNos ||
                  "",
              );
              const othersNos = parseNumber(
                monthlyData?.diversityInLeadership?.othersNos ||
                  prevState?.diversityInLeadership?.othersNos ||
                  "",
              );
              const totalNos = maleNos + femaleNos + othersNos;

              if (totalNos > 0) {
                return ((femaleNos / totalNos) * 100).toFixed(2);
              }
              return monthlyData?.diversityInLeadership?.femalePercentage === 0
                ? 0
                : monthlyData?.diversityInLeadership?.femalePercentage ||
                    prevState?.diversityInLeadership?.femalePercentage ||
                    "";
            })(),
            othersNos:
              monthlyData?.diversityInLeadership?.othersNos === 0
                ? 0
                : monthlyData?.diversityInLeadership?.othersNos ||
                  prevState?.diversityInLeadership?.othersNos ||
                  "",
            othersPercentage: (() => {
              const parseNumber = (val) => {
                if (!val || val === "" || val === null || val === undefined)
                  return 0;
                const parsed = parseFloat(removeCommas(val));
                return isNaN(parsed) ? 0 : parsed;
              };

              const maleNos = parseNumber(
                monthlyData?.diversityInLeadership?.maleNos ||
                  prevState?.diversityInLeadership?.maleNos ||
                  "",
              );
              const femaleNos = parseNumber(
                monthlyData?.diversityInLeadership?.femaleNos ||
                  prevState?.diversityInLeadership?.femaleNos ||
                  "",
              );
              const othersNos = parseNumber(
                monthlyData?.diversityInLeadership?.othersNos ||
                  prevState?.diversityInLeadership?.othersNos ||
                  "",
              );
              const totalNos = maleNos + femaleNos + othersNos;

              if (totalNos > 0) {
                return ((othersNos / totalNos) * 100).toFixed(2);
              }
              return monthlyData?.diversityInLeadership?.othersPercentage === 0
                ? 0
                : monthlyData?.diversityInLeadership?.othersPercentage ||
                    prevState?.diversityInLeadership?.othersPercentage ||
                    "";
            })(),
            totalNos: (() => {
              const parseNumber = (val) => {
                if (!val || val === "" || val === null || val === undefined)
                  return 0;
                const parsed = parseFloat(removeCommas(val));
                return isNaN(parsed) ? 0 : parsed;
              };

              const maleNos = parseNumber(
                monthlyData?.diversityInLeadership?.maleNos ||
                  prevState?.diversityInLeadership?.maleNos ||
                  "",
              );
              const femaleNos = parseNumber(
                monthlyData?.diversityInLeadership?.femaleNos ||
                  prevState?.diversityInLeadership?.femaleNos ||
                  "",
              );
              const othersNos = parseNumber(
                monthlyData?.diversityInLeadership?.othersNos ||
                  prevState?.diversityInLeadership?.othersNos ||
                  "",
              );
              const calculatedTotal = maleNos + femaleNos + othersNos;

              return calculatedTotal > 0
                ? calculatedTotal.toString()
                : monthlyData?.diversityInLeadership?.totalNos === 0
                  ? 0
                  : monthlyData?.diversityInLeadership?.totalNos ||
                    prevState?.diversityInLeadership?.totalNos ||
                    "";
            })(),
            totalPercentage: (() => {
              const parseNumber = (val) => {
                if (!val || val === "" || val === null || val === undefined)
                  return 0;
                const parsed = parseFloat(removeCommas(val));
                return isNaN(parsed) ? 0 : parsed;
              };

              const maleNos = parseNumber(
                monthlyData?.diversityInLeadership?.maleNos ||
                  prevState?.diversityInLeadership?.maleNos ||
                  "",
              );
              const femaleNos = parseNumber(
                monthlyData?.diversityInLeadership?.femaleNos ||
                  prevState?.diversityInLeadership?.femaleNos ||
                  "",
              );
              const othersNos = parseNumber(
                monthlyData?.diversityInLeadership?.othersNos ||
                  prevState?.diversityInLeadership?.othersNos ||
                  "",
              );
              const totalNos = maleNos + femaleNos + othersNos;

              if (totalNos > 0) {
                // Total Percentage should always be 100% since it's the sum of all parts
                return "100";
              }
              return monthlyData?.diversityInLeadership?.totalPercentage === 0
                ? 0
                : monthlyData?.diversityInLeadership?.totalPercentage ||
                    prevState?.diversityInLeadership?.totalPercentage ||
                    "";
            })(),
          },
        }));

        const toBoolean = (val, defaultValue = false) => {
          if (typeof val === "boolean") return val;
          if (typeof val === "string") {
            const normalized = val.trim().toLowerCase();
            if (normalized === "true") return true;
            if (normalized === "false") return false;
          }
          return defaultValue;
        };

        // Update annual form data
        setAnnualFormData((prevState) => {
          const hasPrevDoc =
            typeof prevPolicyDocUrl === "string" &&
            prevPolicyDocUrl.trim().length > 0;
          const hasCurrentDoc =
            typeof annualData.equalOpportunityPolicy?.documentUrl ===
              "string" &&
            annualData.equalOpportunityPolicy.documentUrl.trim().length > 0;
          const shouldUsePrevPolicy = hasPrevDoc && !hasCurrentDoc;

          const resolvedEqualOpportunityStatus = toBoolean(
            annualData.equalOpportunityPolicy?.status,
            prevPolicyStatus !== undefined
              ? toBoolean(prevPolicyStatus, true)
              : toBoolean(prevState.equalOpportunityPolicy.status, true),
          );

          return {
            genderDiversity: {
              male:
                fetchedData?.genderDiversity?.male === 0
                  ? 0
                  : annualData?.genderDiversity?.male ||
                    prevState?.genderDiversity?.male ||
                    "",
              female:
                annualData?.genderDiversity?.female === 0
                  ? 0
                  : annualData?.genderDiversity?.female ||
                    prevState?.genderDiversity?.female ||
                    "",
              others:
                annualData.genderDiversity?.others === 0
                  ? 0
                  : annualData.genderDiversity?.others ||
                    prevState.genderDiversity.others ||
                    "",
            },
            ethnicDiversity:
              annualData?.ethnicDiversity?.length > 0
                ? annualData.ethnicDiversity
                : prevState.ethnicDiversity || [
                    { groupName: "", diversityPercentage: "" },
                  ],
            employeesWithDisabilities:
              annualData.employeesWithDisabilities === 0
                ? 0
                : annualData.employeesWithDisabilities ||
                  prevState.employeesWithDisabilities ||
                  "",
            diversityInLeadership: {
              gender:
                annualData.diversityInLeadership?.gender?.length > 0
                  ? annualData.diversityInLeadership.gender
                  : prevState.diversityInLeadership.gender || [
                      { name: "Male", diversityPercentage: "" },
                      { name: "Female", diversityPercentage: "" },
                      { name: "Others", diversityPercentage: "" },
                    ],
              ethnicity:
                annualData.diversityInLeadership?.ethnicity?.length > 0
                  ? annualData.diversityInLeadership.ethnicity
                  : prevState.diversityInLeadership.ethnicity || [
                      { groupName: "", diversityPercentage: "" },
                    ],
            },
            equalOpportunityPolicy: {
              status: shouldUsePrevPolicy
                ? true
                : resolvedEqualOpportunityStatus,
              isRevised: shouldUsePrevPolicy
                ? false
                : annualData.equalOpportunityPolicy?.isRevised !== undefined
                  ? toBoolean(
                      annualData.equalOpportunityPolicy.isRevised,
                      false,
                    )
                  : prevState.equalOpportunityPolicy.isRevised !== undefined
                    ? toBoolean(
                        prevState.equalOpportunityPolicy.isRevised,
                        false,
                      )
                    : false,
              documentUrl: shouldUsePrevPolicy
                ? prevPolicyDocUrl
                : resolvedEqualOpportunityStatus === false
                  ? ""
                  : sanitizeDocumentUrl(
                      annualData.equalOpportunityPolicy?.documentUrl || ""
                    ) ||
                    // Fallback to previously fetched evidence if current is empty
                    (typeof prevPolicyDocUrl === "string" &&
                    prevPolicyDocUrl.trim()
                      ? prevPolicyDocUrl
                      : undefined) ||
                    sanitizeDocumentUrl(
                      prevState.equalOpportunityPolicy.documentUrl || ""
                    ) ||
                    "",
              remark: shouldUsePrevPolicy
                ? prevPolicyRemark || ""
                : annualData.equalOpportunityPolicy?.status === false
                  ? null
                  : annualData.equalOpportunityPolicy?.remark ||
                    (typeof prevPolicyRemark === "string" &&
                    prevPolicyRemark.trim()
                      ? prevPolicyRemark
                      : undefined) ||
                    prevState.equalOpportunityPolicy.remark ||
                    "",
              statusremark:
                annualData.equalOpportunityPolicy?.statusremark ||
                // prevPolicyStatusRemark ||
                 prevState.equalOpportunityPolicy.statusremark ||
                "",
              effectiveDate:
                (shouldUsePrevPolicy
                  ? prevPolicyEffectiveDate || ""
                  : annualData.equalOpportunityPolicy?.effectiveDate) ||
                (typeof prevPolicyEffectiveDate === "string" &&
                prevPolicyEffectiveDate
                  ? prevPolicyEffectiveDate
                  : undefined) ||
                prevState.equalOpportunityPolicy.effectiveDate ||
                "",
            },
            genderBasedViolencePolicy: {
              status:
                annualData.genderBasedViolencePolicy?.status !== undefined
                  ? annualData.genderBasedViolencePolicy.status
                  : prevGenderPolicyStatus !== undefined
                    ? toBoolean( true)
                  : prevState.genderBasedViolencePolicy.status !== undefined
                    ? prevState.genderBasedViolencePolicy.status
                    : true,
              isRevised:
                annualData.genderBasedViolencePolicy?.isRevised !== undefined
                  ? toBoolean(
                      annualData.genderBasedViolencePolicy.isRevised,
                      false,
                    )
                  : prevGenderPolicyIsRevised !== undefined
                    ? toBoolean(prevGenderPolicyIsRevised, false)
                  : prevState.genderBasedViolencePolicy.isRevised !== undefined
                    ? toBoolean(
                        prevState.genderBasedViolencePolicy.isRevised,
                        false,
                      )
                    : false,
              documentUrl:
                sanitizeDocumentUrl(
                  annualData.genderBasedViolencePolicy?.documentUrl || ""
                ) ||
                (typeof prevGenderPolicyDocUrl === "string" &&
                prevGenderPolicyDocUrl.trim()
                  ? prevGenderPolicyDocUrl
                  : undefined) ||
                sanitizeDocumentUrl(
                  prevState.genderBasedViolencePolicy.documentUrl || ""
                ) ||
                "",
              remark:
                annualData.genderBasedViolencePolicy?.status === false
                  ? null
                  : annualData.genderBasedViolencePolicy?.remark ||
                    (typeof prevGenderPolicyRemark === "string" &&
                    prevGenderPolicyRemark.trim()
                      ? prevGenderPolicyRemark
                      : undefined) ||
                    prevState.genderBasedViolencePolicy.remark ||
                    "",
              statusremark:
                annualData.genderBasedViolencePolicy?.statusremark ||
                // prevGenderPolicyStatusRemark ||
                prevState.genderBasedViolencePolicy.statusremark ||
                "",
              effectiveDate:
                annualData.genderBasedViolencePolicy?.status === false
                  ? ""
                  : annualData.genderBasedViolencePolicy?.effectiveDate ||
                    (typeof prevGenderPolicyEffectiveDate === "string" &&
                    prevGenderPolicyEffectiveDate
                      ? prevGenderPolicyEffectiveDate
                      : undefined) ||
                    prevState.genderBasedViolencePolicy.effectiveDate ||
                    "",
            },
          };
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [searchParams],
  );

  useEffect(() => {
    fetchData(id);
  }, [id, fetchData]);

  const handleChange = (
    e,
    section,
    nestedSection = null,
    index = null,
    field = null,
    type, // 'gender' or 'ethnicity'
  ) => {
    const { value } = e.target;
    const normalizedValue = removeCommas(value);
    const inputValue = type === "number" ? formatWithCommas(normalizedValue) : normalizedValue;
    console.log(
      type,
      section,
      nestedSection,
      index,
      field,
      value,
      "checking in handle change",
    );
    if (type === "percentage") {
      // Allow only numbers with an optional decimal
      if (!/^\d*\.?\d*$/.test(normalizedValue)) return;

      // Restrict range between 0 and 100
      if (parseFloat(normalizedValue) < 0 || parseFloat(normalizedValue) > 100) return;
    }
    if (type === "number") {
      // Allow only numbers
      if (!/^\d*\.?\d*$/.test(normalizedValue)) return;
    }
    setFormData((prev) => {
      const updatedData = { ...prev };

      // Handle monthly module fields (direct properties)
      if (module === "monthly" && !nestedSection && index === null) {
        // For monthly, handle direct properties like maleNos, malePercentage, etc.
        if (
          section === "genderDiversity" ||
          section === "diversityInLeadership"
        ) {
          updatedData[section] = {
            ...updatedData[section],
            [e.target.name]: inputValue,
          };

          // Auto-calculate totalNos and percentages for diversityInLeadership when maleNos, femaleNos, or othersNos change
          if (
            section === "diversityInLeadership" &&
            (e.target.name === "maleNos" ||
              e.target.name === "femaleNos" ||
              e.target.name === "othersNos")
          ) {
            const parseNumber = (val) => {
              if (!val || val === "" || val === null || val === undefined)
                return 0;
              const parsed = parseFloat(removeCommas(val));
              return isNaN(parsed) ? 0 : parsed;
            };

            const maleNos = parseNumber(
              updatedData.diversityInLeadership.maleNos,
            );
            const femaleNos = parseNumber(
              updatedData.diversityInLeadership.femaleNos,
            );
            const othersNos = parseNumber(
              updatedData.diversityInLeadership.othersNos,
            );
            const totalNos = maleNos + femaleNos + othersNos;

            // Calculate totalNos
            updatedData.diversityInLeadership.totalNos = formatWithCommas(totalNos.toString());

            // Calculate percentages: (nos / totalNos) * 100
            if (totalNos > 0) {
              const malePercentage = ((maleNos / totalNos) * 100).toFixed(2);
              const femalePercentage = ((femaleNos / totalNos) * 100).toFixed(
                2,
              );
              const othersPercentage = ((othersNos / totalNos) * 100).toFixed(
                2,
              );

              updatedData.diversityInLeadership.malePercentage = malePercentage;
              updatedData.diversityInLeadership.femalePercentage =
                femalePercentage;
              updatedData.diversityInLeadership.othersPercentage =
                othersPercentage;

              // Calculate total percentage: should always be 100%
              updatedData.diversityInLeadership.totalPercentage = "100";
            } else {
              updatedData.diversityInLeadership.malePercentage = "";
              updatedData.diversityInLeadership.femalePercentage = "";
              updatedData.diversityInLeadership.othersPercentage = "";
              updatedData.diversityInLeadership.totalPercentage = "";
            }
          }

          setErrors((prev) => ({
            ...prev,
            [section]: {
              ...prev[section],
              [e.target.name]: "",
            },
          }));
          return updatedData;
        }
      }

      // Handle monthly ethnicDiversity (has diversityNos field)
      if (
        module === "monthly" &&
        section === "ethnicDiversity" &&
        index !== null
      ) {
        const parseNumber = (val) => {
          if (!val || val === "" || val === null || val === undefined) return 0;
          const parsed = parseFloat(removeCommas(val));
          return isNaN(parsed) ? 0 : parsed;
        };

        // Update the field value
        updatedData[section] = updatedData[section].map((item, idx) =>
          idx === index ? { ...item, [field]: field === "diversityNos" ? inputValue : normalizedValue } : item,
        );

        // If diversityNos changed, auto-calculate percentage for all items
        if (field === "diversityNos") {
          // Calculate totalDiversity (sum of all diversityNos)
          const totalDiversity = updatedData[section].reduce((sum, item) => {
            return sum + parseNumber(item.diversityNos);
          }, 0);

          // Calculate percentage for each item: (diversityNos / totalDiversity) * 100
          updatedData[section] = updatedData[section].map((item) => {
            const diversityNos = parseNumber(item.diversityNos);
            const percentage =
              totalDiversity > 0
                ? ((diversityNos / totalDiversity) * 100).toFixed(2)
                : "";
            return { ...item, diversityPercentage: percentage };
          });

          // Validate that totalDiversity equals totalEmployees
          if (
            totalDiversity !== totalEmployees &&
            totalDiversity > 0 &&
            totalEmployees > 0
          ) {
            // Show the error ONLY on the row the user is currently editing
            setErrors((prev) => {
              const errorMessage = `Total diversity (${totalDiversity}) must equal total employees (${totalEmployees})`;

              const newErrorArray = updatedData[section].map((_, idx) => {
                if (idx === index) {
                  return {
                    ...(prev.ethnicDiversity?.[idx] || {}),
                    diversityNos: errorMessage,
                  };
                }
                // Clear errors for other rows for this field
                return {
                  ...(prev.ethnicDiversity?.[idx] || {}),
                  diversityNos: "",
                };
              });

              return {
                ...prev,
                ethnicDiversity: newErrorArray,
              };
            });
          } else {
            // Clear all ethnicDiversity errors if validation passes
            setErrors((prev) => ({
              ...prev,
              ethnicDiversity: (prev.ethnicDiversity || []).map(() => ({})),
            }));
          }
        } else {
          // For non-diversityNos fields, clear error only on the edited row/field
          setErrors((prev) => ({
            ...prev,
            ethnicDiversity: (prev.ethnicDiversity || []).map((err, idx) =>
              idx === index ? { ...(err || {}), [field]: "" } : err || {},
            ),
          }));
        }
        return updatedData;
      }

      if (nestedSection) {
        if (["male", "female", "others"].includes(field)) {
          const currentItems = updatedData[section][nestedSection];
          const newItems = currentItems.map((item, idx) =>
            idx === index ? { ...item, [field]: value } : item,
          );
          const sum =
            Number(newItems[index].male || 0) +
            Number(newItems[index].female || 0) +
            Number(newItems[index].others || 0);

          if (sum > 100) {
            // Optionally show an error here
            setErrors((prev) => ({
              ...prev,
              genderDiversity: {
                ...(prev.genderDiversity || {}),
                [e.target.name]:
                  "Sum of Male, Female, and Others should not exceed 100",
              },
            }));
            return prev; // Do not update state
          }
          updatedData[section][nestedSection] = newItems;
        } else if (
          section === "diversityInLeadership" &&
          nestedSection === "gender" &&
          field === "diversityPercentage"
        ) {
          const currentItems = updatedData[section][nestedSection];
          const newItems = currentItems.map((item, idx) =>
            idx === index ? { ...item, diversityPercentage: value } : item,
          );
          const sum = newItems.reduce(
            (acc, item) => acc + Number(item.diversityPercentage || 0),
            0,
          );
          let updatedErrors = (errors?.diversityInLeadership?.gender || []).map(
            (err, idx) => err || {},
          );
          if (sum > 100) {
            updatedErrors[index] = {
              ...updatedErrors[index],
              diversityPercentage:
                "Sum of Male, Female, and Others should not exceed 100",
            };

            setErrors((prevErrors) => ({
              ...prevErrors,
              diversityInLeadership: {
                ...prevErrors.diversityInLeadership,
                gender: updatedErrors,
              },
            }));
            return prev; // Do not update state
          } else {
            updatedErrors[index] = {
              ...updatedErrors[index],
              diversityPercentage: "",
            };

            setErrors((prevErrors) => ({
              ...prevErrors,
              diversityInLeadership: {
                ...prevErrors.diversityInLeadership,
                gender: updatedErrors,
              },
            }));
          }
          updatedData[section][nestedSection] = newItems;
          updatedData[section] = {
            ...updatedData[section],
            [nestedSection]: newItems,
          };
        } else {
          updatedData[section][nestedSection] = updatedData[section][
            nestedSection
          ].map((item, idx) =>
            idx === index ? { ...item, [field]: value } : item,
          );
        }
        updatedData[section] = {
          ...updatedData[section],
          [nestedSection]: updatedData[section][nestedSection].map(
            (item, idx) => (idx === index ? { ...item, [field]: value } : item),
          ),
        };
        setErrors((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [nestedSection]: prev[section]?.[nestedSection]?.map((item, idx) =>
              idx === index ? { ...item, [field]: "" } : item,
            ),
          },
        }));
      } else if (index !== null) {
        updatedData[section] = updatedData[section].map((item, idx) =>
          idx === index ? { ...item, [field]: value } : item,
        );
        setErrors((prev) => ({
          ...prev,
          [section]: prev[section]?.map((item, idx) =>
            idx === index
              ? {
                  ...item,
                  [field]: "",
                }
              : { item: "" },
          ),
        }));
      } else {
        // Special check for genderDiversity sum
        if (section === "genderDiversity") {
          // Calculate the new sum with the updated value
          const newGenderData = {
            ...updatedData.genderDiversity,
            [e.target.name]: value,
          };
          const sum =
            Number(newGenderData.male || 0) +
            Number(newGenderData.female || 0) +
            Number(newGenderData.others || 0);

          if (sum > 100) {
            setErrors((prevErrors) => ({
              ...prevErrors,
              genderDiversity: {
                ...prevErrors.genderDiversity,
                [e.target.name]:
                  "Sum of Male, Female, and Others should not exceed 100%",
              },
            }));
            return prev; // Do not update state
          } else {
            // Clear errors for all fields when sum is valid
            setErrors((prevErrors) => ({
              ...prevErrors,
              genderDiversity: {
                ...prevErrors.genderDiversity,
                [e.target.name]: "",
              },
            }));
          }
          updatedData[section][e.target.name] = value;
        } else {
          updatedData[section][e.target.name] = value;
          setErrors((prev) => ({
            ...prev,
            [section]: {
              ...prev[section],
              [e.target.name]: "",
            },
          }));
        }
      }

      return updatedData;
    });
  };

  const handleAddEthnicDiversity = () => {
    // Add a new empty ethnic diversity row
    setFormData((prev) => ({
      ...prev,
      ethnicDiversity: [
        ...prev.ethnicDiversity,
        { groupName: "", diversityNos: "", diversityPercentage: "" },
      ],
    }));

    // When user clicks Add, clear any "total diversity" errors,
    // so partial totals (less than totalEmployees) don't keep showing
    setErrors((prev) => ({
      ...prev,
      ethnicDiversity: (prev.ethnicDiversity || []).map(() => ({})),
    }));
  };

  const handleAddLeadershipEthnicity = () => {
    setFormData((prev) => ({
      ...prev,
      diversityInLeadership: {
        ...prev.diversityInLeadership,
        ethnicity: [
          ...prev.diversityInLeadership.ethnicity,
          { groupName: "", diversityPercentage: "" },
        ],
      },
    }));
  };

  const handleChangeDisability = (e) => {
    const { value } = e.target;

    if (
      !/^\d*\.?\d*$/.test(value) ||
      parseFloat(value) < 0 ||
      parseFloat(value) > 100
    ) {
      return;
    }

    console.log(value, "check");
    setFormData((prev) => ({
      ...prev,
      employeesWithDisabilities: value,
    }));
    setErrors((prev) => ({
      ...prev,
      employeesWithDisabilities: "",
    }));
  };

  const handlePolicyChange = (e, policy) => {
    // Handle both event objects and direct {target: {name, value}} objects
    const { value, name } = e.target || e;
    console.log(name, "for handling");
    const isStatusChange = name === "status";
    const isEqualOpportunity = policy === "equalOpportunityPolicy";
    const isGenderBasedViolence = policy === "genderBasedViolencePolicy";
    const isRevisionPolicy = isEqualOpportunity || isGenderBasedViolence;
    const isRevisedChange = name === "isRevised";
    const revisedValue = value === "true";

    setFormData((prev) => ({
      ...prev,
      [policy]: {
        ...prev[policy],
        [name]:
          name === "remark" ||
          name === "effectiveDate" ||
          name === "statusremark"
            ? value
            : value === "true"
              ? true
              : false,
        ...(isRevisionPolicy && isRevisedChange && revisedValue
          ? { documentUrl: "", remark: "", effectiveDate: "" }
          : {}),
        ...(isRevisionPolicy && isRevisedChange && !revisedValue
          ? {
              documentUrl:
                (isEqualOpportunity
                  ? existingEqualOpportunityDocUrl ||
                    prevEqualOpportunity.documentUrl
                  : existingGenderBasedViolenceDocUrl ||
                    prevGenderBasedViolence.documentUrl) ||
                prev[policy]?.documentUrl ||
                "",
              // Also restore previous remark/effectiveDate when toggling back to "No"
              remark:
                typeof (isEqualOpportunity
                  ? prevEqualOpportunity.remark
                  : prevGenderBasedViolence.remark) === "string"
                  ? isEqualOpportunity
                    ? prevEqualOpportunity.remark
                    : prevGenderBasedViolence.remark
                  : prev[policy]?.remark || "",
              ...(isEqualOpportunity
                ? {
                    effectiveDate:
                      typeof prevEqualOpportunity.effectiveDate === "string"
                        ? prevEqualOpportunity.effectiveDate
                        : prev[policy]?.effectiveDate || "",
                  }
                : {
                    effectiveDate:
                      typeof prevGenderBasedViolence.effectiveDate === "string"
                        ? prevGenderBasedViolence.effectiveDate
                        : prev[policy]?.effectiveDate || "",
                  }),
            }
          : {}),
        // Clear documentUrl, remark, isRevised, and effectiveDate when status is false
        // ...(isStatusChange && value === "false"
        //   ? {
        //       documentUrl: "",
        //       remark: null,
        //       ...(isRevisionPolicy ? { isRevised: false } : {}),
        //       ...(isEqualOpportunity ? { effectiveDate: "" } : {}),
        //     }
        //   : {}),
      },
    }));
    setErrors((prev) => {
      const next = { ...prev };
      if (
        policy !== "equalOpportunityPolicy" &&
        policy !== "genderBasedViolencePolicy"
      ) {
        return {
          ...next,
          [policy]: {
            ...(typeof next[policy] === "object" && next[policy]
              ? next[policy]
              : {}),
            [name]: "",
          },
        };
      }
      if (name === "status" || name === "isRevised") {
        delete next[policy];
        return next;
      }
      const cur =
        typeof next[policy] === "object" && next[policy] ? { ...next[policy] } : {};
      if (name === "remark" && String(value || "").trim()) delete cur.remark;
      if (name === "effectiveDate") delete cur.effectiveDate;
      if (Object.keys(cur).length) next[policy] = cur;
      else delete next[policy];
      return next;
    });
  };

  const handleFileChange = (file, policy) => {
    if (
      policy === "equalOpportunityPolicy" ||
      policy === "genderBasedViolencePolicy"
    ) {
      setDeletedPolicyDocs((prev) => ({
        ...prev,
        [policy]: !hasUploadedDocument(file),
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [policy]: {
        ...prev[policy],
        documentUrl: file,
      },
    }));
    if (
      (policy === "equalOpportunityPolicy" ||
        policy === "genderBasedViolencePolicy") &&
      hasUploadedDocument(file)
    ) {
      setErrors((prev) => {
        const next = { ...prev };
        const cur =
          typeof next[policy] === "object" && next[policy] ? { ...next[policy] } : {};
        delete cur.documentUrl;
        if (Object.keys(cur).length) next[policy] = cur;
        else delete next[policy];
        return next;
      });
    } else if (
      policy !== "equalOpportunityPolicy" &&
      policy !== "genderBasedViolencePolicy"
    ) {
      setErrors((prev) => ({
        ...prev,
        [policy]: {
          ...prev[policy],
          documentUrl: "",
        },
      }));
    }
  };

  const handleDelete = () => {
    const fieldName = deleteData.fieldName;
    const index = deleteData.index;
    setFormData((prev) => {
      const fieldKeys = fieldName.split("."); // Split fieldName into an array
      if (fieldKeys.length === 2) {
        // If there's a subfield (e.g., "diversityInLeadership.ethnicity")
        const [mainField, subField] = fieldKeys;

        return {
          ...prev,
          [mainField]: {
            ...prev[mainField],
            [subField]: prev[mainField][subField].filter((_, i) => i !== index),
          },
        };
      } else {
        // If there's no subfield (e.g., "ethnicDiversity")
        return {
          ...prev,
          [fieldName]: prev[fieldName].filter((_, i) => i !== index),
        };
      }
    });
    setDeleteDialog(false);
  };
  const validation = () => {
    let newErrors = {}; // Initialize an empty error object
    let hasErrors = false;

    const isEmpty = (value) =>
      value === "" || value === null || value === undefined;

    const validateRecursive = (data, errorsObj) => {
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          let arrayErrors = value.map((item, index) => {
            if (typeof item === "object" && item !== null) {
              let nestedErrors = {};
              validateRecursive(item, nestedErrors);

              // ✅ Store errors at the correct index
              return Object.keys(nestedErrors).length > 0 ? nestedErrors : null;
            } else {
              return isEmpty(item) ? textError : null;
            }
          });

          // ✅ Only store the errors in `errorsObj` if any errors exist
          if (arrayErrors.some((error) => error !== null)) {
            errorsObj[key] = arrayErrors; // ✅ Ensure errors align with indexes
            hasErrors = true;
          }
        } else if (typeof value === "object" && value !== null) {
          let nestedErrors = {};

          // ✅ Validate policy fields only if `status: true`
          if (
            ["genderBasedViolencePolicy", "equalOpportunityPolicy"].includes(
              key,
            )
          ) {
            if (value.status) {
              const showRev =
                key === "equalOpportunityPolicy"
                  ? shouldShowAnnualPolicyRevised(
                      !isFirstEntry,
                      previousYearEqualOpportunityDocUrl,
                    )
                  : shouldShowAnnualPolicyRevised(
                      !isFirstEntry,
                      previousYearGenderViolenceDocUrl,
                    );
              const polErr = annualPolicyYesFieldErrors(
                value,
                entryYear,
                showRev,
              );
              Object.assign(nestedErrors, polErr);
              if (Object.keys(polErr).length) hasErrors = true;
            }
          } else if (key === "diversityInLeadership") {
            if (module === "monthly") {
              const monthlyFieldErrors = {};
              const requiredFields = ["maleNos", "femaleNos", "othersNos"];
              requiredFields.forEach((fieldKey) => {
                if (isEmpty(value?.[fieldKey])) {
                  monthlyFieldErrors[fieldKey] = textError;
                  hasErrors = true;
                }
              });
              if (Object.keys(monthlyFieldErrors).length > 0) {
                errorsObj[key] = monthlyFieldErrors;
              }
              return;
            }

            const diversityErrors = {};

            // Validate each sub-array (gender, ethnicity)
            ["gender", "ethnicity"].forEach((subKey) => {
              const group = value[subKey];
              if (Array.isArray(group)) {
                let groupErrors = [];
                let total = 0;
                group.forEach((item, index) => {
                  const itemErrors = {};
                  const perc = item.diversityPercentage;

                  if (isEmpty(perc)) {
                    itemErrors.diversityPercentage = textError;
                    hasErrors = true;
                  } else if (Number(perc) < 0 || Number(perc) > 100) {
                    itemErrors.diversityPercentage =
                      "Percentage must be between 0 and 100";
                    hasErrors = true;
                  } else {
                    total += Number(perc);
                  }

                  // Optional: check empty name/groupName
                  if (subKey === "ethnicity" && isEmpty(item.groupName)) {
                    itemErrors.groupName = "Group name is required";
                    hasErrors = true;
                  }

                  groupErrors[index] =
                    Object.keys(itemErrors).length > 0 ? itemErrors : null;
                });

                // Optional: total % check
                if (total > 100) {
                  groupErrors = group.map((_, index) => ({
                    diversityPercentage:
                      "Total percentage should not exceed 100",
                  }));
                  hasErrors = true;
                }

                if (groupErrors.some((err) => err !== null)) {
                  diversityErrors[subKey] = groupErrors;
                }
              }
            });

            if (Object.keys(diversityErrors).length > 0) {
              errorsObj[key] = diversityErrors;
              hasErrors = true;
            }
          } else {
            validateRecursive(value, nestedErrors);
          }

          if (Object.keys(nestedErrors).length > 0) {
            errorsObj[key] = nestedErrors;
            hasErrors = true;
          }
        } else if (isEmpty(value)) {
          errorsObj[key] = textError;
          hasErrors = true;
        }
      });
    };

    // Only validate fields relevant to the current module
    if (module === "annual") {
      // For annual module, only validate annual fields (policies)
      validateRecursive(
        {
          equalOpportunityPolicy: formData.equalOpportunityPolicy,
          genderBasedViolencePolicy: formData.genderBasedViolencePolicy,
        },
        newErrors,
      );

    } else {
      // For monthly module, only validate monthly fields
      validateRecursive(
        {
          genderDiversity: formData.genderDiversity,
          ethnicDiversity: formData.ethnicDiversity,
          diversityInLeadership: formData.diversityInLeadership,
        },
        newErrors,
      );

      // Additional validation: totalDiversity must equal totalEmployees
      const parseNumber = (val) => {
        if (!val || val === "" || val === null || val === undefined) return 0;
        const parsed = parseFloat(removeCommas(val));
        return isNaN(parsed) ? 0 : parsed;
      };

      const totalDiversity = (formData.ethnicDiversity || []).reduce(
        (sum, item) => {
          return sum + parseNumber(item.diversityNos);
        },
        0,
      );

      if (
        totalDiversity > 0 &&
        totalEmployees > 0 &&
        totalDiversity !== totalEmployees
      ) {
        // Add error to all diversityNos fields
        if (!newErrors.ethnicDiversity) {
          newErrors.ethnicDiversity = [];
        }
        formData.ethnicDiversity.forEach((item, index) => {
          if (item.diversityNos && item.diversityNos !== "") {
            if (!newErrors.ethnicDiversity[index]) {
              newErrors.ethnicDiversity[index] = {};
            }
            newErrors.ethnicDiversity[index].diversityNos =
              `Total diversity (${totalDiversity}) must equal total employees (${totalEmployees})`;
            hasErrors = true;
          }
        });
      }
    }

    const removeEmptyErrors = (value) => {
      if (Array.isArray(value)) {
        // Preserve array indices so row-wise field errors map correctly in UI.
        const cleanedArray = value.map((item) => removeEmptyErrors(item));
        const hasAnyError = cleanedArray.some(
          (item) =>
            item !== null &&
            item !== undefined &&
            item !== "" &&
            !(
              typeof item === "object" &&
              !Array.isArray(item) &&
              Object.keys(item).length === 0
            ) &&
            !(Array.isArray(item) && item.length === 0),
        );
        return hasAnyError ? cleanedArray : undefined;
      }

      if (value && typeof value === "object") {
        const cleanedObject = Object.entries(value).reduce(
          (acc, [key, val]) => {
            const cleanedVal = removeEmptyErrors(val);
            if (
              cleanedVal !== undefined &&
              cleanedVal !== null &&
              cleanedVal !== "" &&
              !(
                typeof cleanedVal === "object" &&
                !Array.isArray(cleanedVal) &&
                Object.keys(cleanedVal).length === 0
              ) &&
              !(Array.isArray(cleanedVal) && cleanedVal.length === 0)
            ) {
              acc[key] = cleanedVal;
            }
            return acc;
          },
          {},
        );
        return Object.keys(cleanedObject).length > 0
          ? cleanedObject
          : undefined;
      }

      return value === "" || value === null || value === undefined
        ? undefined
        : value;
    };

    const cleanedErrors = removeEmptyErrors(newErrors) || {};
    const hasRealErrors = Object.keys(cleanedErrors).length > 0;

    console.log(cleanedErrors, "newErrors");
    setErrors(cleanedErrors);
    return hasRealErrors;
  };

  const handleSubmit = async () => {
    if (validation()) return;

    let cleanedFormData = {};

    if (module === "annual") {
      // For annual module, only send annual fields (policies)
      cleanedFormData = {
        equalOpportunityPolicy: {
          ...annualFormData.equalOpportunityPolicy,
          ...(annualFormData.equalOpportunityPolicy.status === false
            ? {
                documentUrl: "",
                remark: null,
                isRevised: false,
                effectiveDate: "",
              }
            : { statusremark: "" }),
        },
        genderBasedViolencePolicy: {
          ...annualFormData.genderBasedViolencePolicy,
          ...(annualFormData.genderBasedViolencePolicy.status === false
            ? { documentUrl: "", remark: null, isRevised: false, effectiveDate: "" }
            : { statusremark: "" }),
        },
      };
    } else {
      // For monthly module, only send monthly fields
      cleanedFormData = {
        genderDiversity: monthlyFormData.genderDiversity,
        ethnicDiversity: monthlyFormData.ethnicDiversity,
        diversityInLeadership: monthlyFormData.diversityInLeadership,
      };
    }

    const payload = {
      stepsCompleted: {
        diversityAndInclusion: true,
      },
      diversityAndInclusion: stripCommaNumericValuesDeep(cleanedFormData),
    };

    await put(`/social/updateSocial?Id=${id}`, payload)
      .then((response) => {
        // Navigate to next step with module parameter
        const entryTypeParam = module === "annual" ? "annual" : "monthly";
        navigate(`/social/data/community-engagement/${id}`);
      })
      .catch((error) => {
        console.error("Error updating data:", error);
        const apiErrors =
          error?.response?.data?.errors ||
          error?.response?.data?.data?.errors ||
          error?.response?.data?.error;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(apiErrors);
        }
      });
  };

  const getDisplayedPolicyDocument = (policyKey) => {
    if (policyKey === "equalOpportunityPolicy") {
      if (deletedPolicyDocs.equalOpportunityPolicy) {
        return formData.equalOpportunityPolicy.documentUrl || "";
      }
      return (
        formData.equalOpportunityPolicy.documentUrl ||
        (formData.equalOpportunityPolicy.isRevised
          ? ""
          : existingEqualOpportunityDocUrl || prevEqualOpportunity.documentUrl || "")
      );
    }
    if (policyKey === "genderBasedViolencePolicy") {
      if (deletedPolicyDocs.genderBasedViolencePolicy) {
        return formData.genderBasedViolencePolicy.documentUrl || "";
      }
      return (
        formData.genderBasedViolencePolicy.documentUrl ||
        (formData.genderBasedViolencePolicy.isRevised
          ? ""
          : existingGenderBasedViolenceDocUrl ||
            prevGenderBasedViolence.documentUrl ||
            "")
      );
    }
    return "";
  };

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      <DeleteModal
        onOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
      />
      {/* <div style={{ padding: "1rem 0" }}>
        <span
          style={{
            fontSize: "14px",
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Data{" "}
        </span>
        <span>
          <ChevronRightIcon />
        </span>{" "}
        <span
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "rgba(0, 0, 0, 1)",
          }}
        >
          Add New Entry
        </span>
      </div>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
        Add New Entry
      </Typography> */}

      {/* Module Selection */}
      <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
            
          </Typography>
          <RadioGroup
            value={module}
            onChange={(e) => setModule(e.target.value)}
            sx={{ display: "flex", flexDirection: "row", gap: 2 }}
          >
            <FormControlLabel
              value="monthly"
              control={<Radio sx={socialRadioSx} />}
              label="Monthly Module"
              disabled={module === "annual"}
            />
            <FormControlLabel
              value="annual"
              control={<Radio sx={socialRadioSx} />}
              label="Annual Module"
              disabled={module === "monthly"}
            />
          </RadioGroup>
        </Box> */}
      </Box>

      <div style={{ margin: "1.2% 0" }}>
        <p className={styles.form_headingStrongUpper}>
          B. DIVERSITY AND INCLUSION
        </p>

        {/* Monthly Module Section */}
        {module === "monthly" && (
          <>
            {/* Gender Diversity - Monthly */}
            <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
              1. Gender Diversity
            </p>
            <div style={{ padding: "0 1rem" }}>
              <div
                style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}
              >
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Male (No's)
                  </Typography>
                  <TextField
                    name="maleNos"
                    value={formData.genderDiversity.maleNos || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "genderDiversity",
                        null,
                        null,
                        null,
                        "number",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.genderDiversity?.maleNos}
                  />
                  {errors?.genderDiversity?.maleNos && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.genderDiversity?.maleNos}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Percentage (%)
                  </Typography>
                  <TextField
                    name="malePercentage"
                    value={formData.genderDiversity.malePercentage || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "genderDiversity",
                        null,
                        null,
                        null,
                        "percentage",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.genderDiversity?.malePercentage}
                  />
                  {errors?.genderDiversity?.malePercentage && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.genderDiversity?.malePercentage}
                    </FormHelperText>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: "1rem" }}>
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Female (No's)
                  </Typography>
                  <TextField
                    name="femaleNos"
                    value={formData.genderDiversity.femaleNos || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "genderDiversity",
                        null,
                        null,
                        null,
                        "number",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.genderDiversity?.femaleNos}
                  />
                  {errors?.genderDiversity?.femaleNos && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.genderDiversity?.femaleNos}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Percentage (%)
                  </Typography>
                  <TextField
                    name="femalePercentage"
                    value={formData.genderDiversity.femalePercentage || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "genderDiversity",
                        null,
                        null,
                        null,
                        "percentage",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.genderDiversity?.femalePercentage}
                  />
                  {errors?.genderDiversity?.femalePercentage && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.genderDiversity?.femalePercentage}
                    </FormHelperText>
                  )}
                </div>
              </div>
            </div>

            <p
              className={styles.form_heading}
              style={{ fontWeight: "bold", marginTop: "1.5rem" }}
            >
              2. Ethnic Diversity (breakdown by ethnic groups):
            </p>
            <div style={{ padding: "0 1rem" }}>
              <div style={{ display: "flex", gap: "1rem", marginTop: "10px" }}>
                <p
                  style={{
                    width: "5%",
                    fontSize: "14px",
                    fontWeight: "400",
                    marginBottom: "0px",
                    marginLeft: "1px",
                  }}
                >
                  S.No
                </p>
                <p
                  style={{
                    width: "30%",
                    fontSize: "14px",
                    fontWeight: "400",
                    marginBottom: "0px",
                    marginLeft: "1px",
                  }}
                >
                  Ethnic Group
                </p>
                <p
                  style={{
                    width: "30%",
                    fontSize: "14px",
                    fontWeight: "400",
                    marginBottom: "0px",
                    marginLeft: "1px",
                  }}
                >
                  Diversity (No's)
                </p>
                <p
                  style={{
                    width: "30%",
                    fontSize: "14px",
                    fontWeight: "400",
                    marginBottom: "0px",
                    marginLeft: "4px",
                  }}
                >
                  Diversity (%)
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                {(formData?.ethnicDiversity || []).map((item, index) => (
                  <div style={{ display: "flex", width: "102%" }}>
                    <div
                      key={index}
                      style={{ display: "flex", gap: "1rem", width: "101%" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "5%",
                          height: "2.5rem",
                          border: "1px solid rgba(116, 117, 126, 1)",
                          borderRadius: "4px",
                        }}
                      >
                        {index + 1}.
                      </div>
                      <div style={{ width: "30%" }}>
                        <TextField
                          name="groupName"
                          value={item.groupName}
                          onChange={(e) =>
                            handleChange(
                              e,
                              "ethnicDiversity",
                              null,
                              index,
                              "groupName",
                              "text",
                            )
                          }
                          variant="outlined"
                          type="text"
                          fullWidth
                          placeholder="Enter"
                          size="small"
                          error={!!getEthnicDiversityFieldError(index, "groupName")}
                          helperText={getEthnicDiversityFieldError(index, "groupName")}
                          FormHelperTextProps={{
                            sx: { color: "#D34040", fontSize: "14px", marginLeft: 0 },
                          }}
                        />
                      </div>
                      <div style={{ width: "30%" }}>
                        <TextField
                          name="diversityNos"
                          value={item.diversityNos || ""}
                          onChange={(e) =>
                            handleChange(
                              e,
                              "ethnicDiversity",
                              null,
                              index,
                              "diversityNos",
                              "number",
                            )
                          }
                          variant="outlined"
                          type="text"
                          fullWidth
                          placeholder="Enter Number"
                          size="small"
                          error={!!getEthnicDiversityFieldError(index, "diversityNos")}
                          helperText={getEthnicDiversityFieldError(index, "diversityNos")}
                          FormHelperTextProps={{
                            sx: { color: "#D34040", fontSize: "14px", marginLeft: 0 },
                          }}
                        />
                      </div>
                      <div style={{ width: "30%" }}>
                        <TextField
                          name="diversityPercentage"
                          value={
                            item.diversityPercentage === 0
                              ? 0
                              : item.diversityPercentage || ""
                          }
                          disabled={true}
                          variant="outlined"
                          type="text"
                          fullWidth
                          placeholder="Auto-calculated"
                          size="small"
                          error={
                            !!getEthnicDiversityFieldError(
                              index,
                              "diversityPercentage",
                            )
                          }
                          helperText={getEthnicDiversityFieldError(
                            index,
                            "diversityPercentage",
                          )}
                          FormHelperTextProps={{
                            sx: { color: "#D34040", fontSize: "14px", marginLeft: 0 },
                          }}
                        />
                      </div>
                      <div></div>
                    </div>
                    <div style={{ width: "1%" }}>
                      {!(
                        index === 0 &&
                        (!item.groupName || item.groupName === "") &&
                        (!item.diversityNos || item.diversityNos === "") &&
                        (!item.diversityPercentage ||
                          item.diversityPercentage === "")
                      ) && (
                        <DeleteIcon
                          sx={{
                            marginRight: "8px",
                            cursor:
                              formData?.ethnicDiversity?.length <= 1
                                ? "not-allowed"
                                : "pointer",
                            fontSize: "large",
                            opacity:
                              formData?.ethnicDiversity?.length <= 1 ? 0.5 : 1,
                          }}
                          onClick={() =>
                            formData?.ethnicDiversity?.length <= 1
                              ? undefined
                              : (setDeleteDialog(true),
                                setDeleteData({
                                  fieldName: "ethnicDiversity",
                                  index: index,
                                }))
                          }
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginRight: "7px",
                }}
              >
                <Button
                  variant="outlined"
                  style={{ marginTop: "1rem",
                    borderColor:!formData?.ethnicDiversity?.length ||
                    !formData.ethnicDiversity[
                      formData.ethnicDiversity.length - 1
                    ]?.groupName ||
                    !formData.ethnicDiversity[
                      formData.ethnicDiversity.length - 1
                    ]?.diversityNos ||
                    // When total ethnic diversity equals (or exceeds) totalEmployees,
                    // prevent adding more rows
                    (totalEmployees > 0 &&
                      totalEthnicDiversityNos >= totalEmployees) ?"#cbd2d8":"#1C486B",
                    color: !formData?.ethnicDiversity?.length ||
                    !formData.ethnicDiversity[
                      formData.ethnicDiversity.length - 1
                    ]?.groupName ||
                    !formData.ethnicDiversity[
                      formData.ethnicDiversity.length - 1
                    ]?.diversityNos ||
                    // When total ethnic diversity equals (or exceeds) totalEmployees,
                    // prevent adding more rows
                    (totalEmployees > 0 &&
                      totalEthnicDiversityNos >= totalEmployees)?"#cbd2d8":"#1C486B",
                   }}
                  onClick={handleAddEthnicDiversity}
                  sx={{ textTransform: "none" }}
                  disabled={
                    !formData?.ethnicDiversity?.length ||
                    !formData.ethnicDiversity[
                      formData.ethnicDiversity.length - 1
                    ]?.groupName ||
                    !formData.ethnicDiversity[
                      formData.ethnicDiversity.length - 1
                    ]?.diversityNos ||
                    // When total ethnic diversity equals (or exceeds) totalEmployees,
                    // prevent adding more rows
                    (totalEmployees > 0 &&
                      totalEthnicDiversityNos >= totalEmployees)
                  }
                >
                  Add <AddIcon style={{ marginLeft: "0.5rem" }} />
                </Button>
              </div>
            </div>

            {/* Diversity in Leadership - Monthly */}
            <p
              className={styles.form_heading}
              style={{ fontWeight: "bold", marginTop: "1rem" }}
            >
              3. Diversity in Leadership Positions:
            </p>
            <div style={{ padding: "0 1rem" }}>
              <div
                style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}
              >
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Male (No's)
                  </Typography>
                  <TextField
                    name="maleNos"
                    value={formData.diversityInLeadership.maleNos || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "diversityInLeadership",
                        null,
                        null,
                        null,
                        "number",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Enter Number"
                    error={!!errors?.diversityInLeadership?.maleNos}
                  />
                  {errors?.diversityInLeadership?.maleNos && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.diversityInLeadership?.maleNos}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Percentage (%)
                  </Typography>
                  <TextField
                    name="malePercentage"
                    value={formData.diversityInLeadership.malePercentage || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "diversityInLeadership",
                        null,
                        null,
                        null,
                        "percentage",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.diversityInLeadership?.malePercentage}
                  />
                  {errors?.diversityInLeadership?.malePercentage && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.diversityInLeadership?.malePercentage}
                    </FormHelperText>
                  )}
                </div>
              </div>
              <div
                style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}
              >
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Female (No's)
                  </Typography>
                  <TextField
                    name="femaleNos"
                    value={formData.diversityInLeadership.femaleNos || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "diversityInLeadership",
                        null,
                        null,
                        null,
                        "number",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Enter Number"
                    error={!!errors?.diversityInLeadership?.femaleNos}
                  />
                  {errors?.diversityInLeadership?.femaleNos && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.diversityInLeadership?.femaleNos}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Percentage (%)
                  </Typography>
                  <TextField
                    name="femalePercentage"
                    value={
                      formData.diversityInLeadership.femalePercentage || ""
                    }
                    onChange={(e) =>
                      handleChange(
                        e,
                        "diversityInLeadership",
                        null,
                        null,
                        null,
                        "percentage",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.diversityInLeadership?.femalePercentage}
                  />
                  {errors?.diversityInLeadership?.femalePercentage && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.diversityInLeadership?.femalePercentage}
                    </FormHelperText>
                  )}
                </div>
              </div>
              <div
                style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}
              >
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Others (No's)
                  </Typography>
                  <TextField
                    name="othersNos"
                    value={formData.diversityInLeadership.othersNos || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "diversityInLeadership",
                        null,
                        null,
                        null,
                        "number",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Enter Number"
                    error={!!errors?.diversityInLeadership?.othersNos}
                  />
                  {errors?.diversityInLeadership?.othersNos && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.diversityInLeadership?.othersNos}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Percentage (%)
                  </Typography>
                  <TextField
                    name="othersPercentage"
                    value={
                      formData.diversityInLeadership.othersPercentage || ""
                    }
                    onChange={(e) =>
                      handleChange(
                        e,
                        "diversityInLeadership",
                        null,
                        null,
                        null,
                        "percentage",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.diversityInLeadership?.othersPercentage}
                  />
                  {errors?.diversityInLeadership?.othersPercentage && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.diversityInLeadership?.othersPercentage}
                    </FormHelperText>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: "1rem" }}>
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Total (No's)
                  </Typography>
                  <TextField
                    name="totalNos"
                    value={formData.diversityInLeadership.totalNos || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "diversityInLeadership",
                        null,
                        null,
                        null,
                        "number",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.diversityInLeadership?.totalNos}
                  />
                  {errors?.diversityInLeadership?.totalNos && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.diversityInLeadership?.totalNos}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ width: "50%" }}>
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Total Percentage (%)
                  </Typography>
                  <TextField
                    name="totalPercentage"
                    value={formData.diversityInLeadership.totalPercentage || ""}
                    onChange={(e) =>
                      handleChange(
                        e,
                        "diversityInLeadership",
                        null,
                        null,
                        null,
                        "percentage",
                      )
                    }
                    variant="outlined"
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Auto-calculated"
                    disabled={true}
                    error={!!errors?.diversityInLeadership?.totalPercentage}
                  />
                  {errors?.diversityInLeadership?.totalPercentage && (
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                      {errors?.diversityInLeadership?.totalPercentage}
                    </FormHelperText>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Annual Module Section */}
        {module === "annual" && (
          <>
            {/* Policies - Annual */}
            <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
              1. Equal Opportunity / No Discrimination Policy:
            </p>
            <FormControl
              component="fieldset"
              error={!!errors?.equalOpportunityPolicy?.status}
            >
              <RadioGroup
                row
                name="equalOpportunityPolicyStatus"
                value={
                  formData.equalOpportunityPolicy.status === false ||
                  formData.equalOpportunityPolicy.status === "false"
                    ? "false"
                    : "true"
                }
                onChange={(e) =>
                  handlePolicyChange(
                    { target: { name: "status", value: e.target.value } },
                    "equalOpportunityPolicy",
                  )
                }
                sx={{ ml: "0.8rem" }}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio sx={socialRadioSx} />}
                  label="Yes"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio sx={socialRadioSx} />}
                  label="No"
                />
              </RadioGroup>
              {errors?.equalOpportunityPolicy?.status && (
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                  {errors?.equalOpportunityPolicy?.status}
                </FormHelperText>
              )}
            </FormControl>
            {formData.equalOpportunityPolicy.status ? (
              <>
                {shouldShowAnnualPolicyRevised(
                  !isFirstEntry,
                  previousYearEqualOpportunityDocUrl,
                ) && (
                  <>
                    <p
                      className={styles.form_heading}
                      style={{ fontWeight: "bold", marginTop: "1rem",marginLeft:"0.8rem" }}
                    >
                      Is the Policy revised?
                    </p>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        name="isRevised"
                        value={
                          formData.equalOpportunityPolicy.isRevised
                            ? "true"
                            : "false"
                        }
                        onChange={(e) =>
                          handlePolicyChange(e, "equalOpportunityPolicy")
                        }
                        sx={{ ml: "0.8rem" }}
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio sx={socialRadioSx} />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio sx={socialRadioSx} />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </>
                )}

                <Box >
                  <Typography
                    component="p"
                    className={styles.form_heading}
                    sx={{ fontWeight: "bold", ml: "0.8rem", mb: 0.5, mt: 1 }}
                  >
                    Upload evidence (for Policy):
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      ml: "1.1rem",
                    }}
                  >
                    <Box
                      sx={{
                        width: hasUploadedDocument(
                          getDisplayedPolicyDocument("equalOpportunityPolicy"),
                        )
                          ? "117px"
                          : "100px",
                      }}
                    >
                      <FileUpload
                        setDocumentUrl={(file) =>
                          handleFileChange(file, "equalOpportunityPolicy")
                        }
                        filesList={getDisplayedPolicyDocument(
                          "equalOpportunityPolicy",
                        )}
                        isDisabled={isAnnualPolicyUploadDisabled(
                          !isFirstEntry,
                          previousYearEqualOpportunityDocUrl,
                        )}
                      />
                      {errors?.equalOpportunityPolicy?.documentUrl ? (
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "14px",
                            minHeight: "20px",
                          }}
                        >
                          {errors?.equalOpportunityPolicy?.documentUrl}
                        </FormHelperText>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        width: "30%",
                        mt: hasUploadedDocument(
                          getDisplayedPolicyDocument("equalOpportunityPolicy"),
                        )
                          ? "15px"
                          : "10px",
                          ml:hasUploadedDocument(
                            getDisplayedPolicyDocument("equalOpportunityPolicy"),
                          ) ? "7.1rem" : "1rem",
                      }}
                    >
                      <TextField
                        fullWidth
                        name="remark"
                        value={formData.equalOpportunityPolicy.remark || ""}
                        onChange={(e) =>
                          handlePolicyChange(e, "equalOpportunityPolicy")
                        }
                        variant="outlined"
                        size="small"
                        placeholder="Enter Additional Text"
                        multiline
                        minRows={1}
                        error={!!errors?.equalOpportunityPolicy?.remark}
                        InputProps={{ sx: { padding: "9px 12px" } }}
                      />
                      <FormHelperText
                        sx={{
                          color: "#D34040",
                          fontSize: "14px",
                          minHeight: "20px",
                        }}
                      >
                        {errors?.equalOpportunityPolicy?.remark || " "}
                      </FormHelperText>
                    </Box>
                  </Box>
                  <Typography
                    component="p"
                    className={styles.form_heading}
                    sx={{ fontWeight: "bold", ml: "0.8rem",mb:"12px" }}
                  >
                    Effective date (for Policy):
                  </Typography>
                  <TextField
                    type="date"
                    name="effectiveDate"
                    value={
                      formData.equalOpportunityPolicy.effectiveDate
                        ? String(
                            formData.equalOpportunityPolicy.effectiveDate,
                          ).split("T")[0]
                        : ""
                    }
                    // inputProps={{
                    //   ...(diversityPolicyEffMin && diversityPolicyEffMax
                    //     ? {
                    //         min: diversityPolicyEffMin,
                    //         max: diversityPolicyEffMax,
                    //       }
                    //     : { max: maxEffectiveDate }),
                    // }}
                    inputProps={{
                      min: "2021-01-01",
                      ...(diversityPolicyEffMax ? { max: diversityPolicyEffMax } : {}),
                    }}
                    onChange={(e) =>
                      handlePolicyChange(e, "equalOpportunityPolicy")
                    }
                    size="small"
                    error={!!errors?.equalOpportunityPolicy?.effectiveDate}
                    sx={{
                      width: "30%",
                      ml: "0.8rem",
                      mb: "0.25rem",
                      "& .MuiInputBase-root": { height: 40 },
                    }}
                  />
                  <FormHelperText
                    sx={{
                      color: "#D34040",
                      fontSize: "14px",
                      marginLeft: "1.1rem",
                      width: "30%",
                    }}
                  >
                    {errors?.equalOpportunityPolicy?.effectiveDate}
                  </FormHelperText>
                </Box>
              </>
            ) : (
              <div style={{ padding: "0 1rem", marginTop: "0.5rem" }}>
                {/* <p
                  className={styles.form_heading}
                  style={{ fontWeight: "bold", marginTop: "1rem" }}
                >
                  Remark
                </p> */}
                <TextField
                  name="statusremark"
                  style={{ width: "30%", marginTop: "7px" }}
                  value={formData.equalOpportunityPolicy.statusremark || ""}
                  onChange={(e) =>
                    handlePolicyChange(e, "equalOpportunityPolicy")
                  }
                  variant="outlined"
                  size="small"
                  placeholder="Enter Additional Text"
                  error={!!errors?.equalOpportunityPolicy?.statusremark}
                  helperText={errors?.equalOpportunityPolicy?.statusremark}
                  FormHelperTextProps={{
                    sx: {
                      marginLeft: 0,
                      paddingLeft: "1px",
                    },
                  }}
                />
              </div>
            )}

            <p
              className={styles.form_heading}
              style={{ fontWeight: "bold", marginTop: "1rem" }}
            >
              2. Gender based violence and harassment Policy:
            </p>
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="genderBasedViolencePolicyStatus"
                value={
                  formData.genderBasedViolencePolicy.status === false ||
                  formData.genderBasedViolencePolicy.status === "false"
                    ? "false"
                    : "true"
                }
                onChange={(e) =>
                  handlePolicyChange(
                    { target: { name: "status", value: e.target.value } },
                    "genderBasedViolencePolicy",
                  )
                }
                sx={{ ml: "0.8rem" }}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio sx={socialRadioSx} />}
                  label="Yes"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio sx={socialRadioSx} />}
                  label="No"
                />
              </RadioGroup>
            </FormControl>
            {formData.genderBasedViolencePolicy.status ? (
              <>
                {shouldShowAnnualPolicyRevised(
                  !isFirstEntry,
                  previousYearGenderViolenceDocUrl,
                ) && (
                  <>
                    <p
                      className={styles.form_heading}
                      style={{ fontWeight: "bold", marginTop: "1rem",ml:"0.8rem" }}
                    >
                      Is the Policy revised?
                    </p>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        name="isRevised"
                        value={
                          formData.genderBasedViolencePolicy.isRevised
                            ? "true"
                            : "false"
                        }
                        onChange={(e) =>
                          handlePolicyChange(e, "genderBasedViolencePolicy")
                        }
                        sx={{ ml: "0.8rem" }}
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio sx={socialRadioSx} />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio sx={socialRadioSx} />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </>
                )}
                <Box >
                  <Typography
                    component="p"
                    className={styles.form_heading}
                    sx={{ fontWeight: "bold", ml: "0.8rem", mb: 0.5, mt: 1 }}
                  >
                    Upload evidence (for Gender Policy):
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      ml: "1.1rem",
                    }}
                  >
                    <Box
                      sx={{
                        width: hasUploadedDocument(
                          getDisplayedPolicyDocument("genderBasedViolencePolicy"),
                        )
                          ? "233px"
                          : "100px",
                      }}
                    >
                      <FileUpload
                        setDocumentUrl={(file) =>
                          handleFileChange(file, "genderBasedViolencePolicy")
                        }
                        filesList={getDisplayedPolicyDocument(
                          "genderBasedViolencePolicy",
                        )}
                        isDisabled={isAnnualPolicyUploadDisabled(
                          !isFirstEntry,
                          previousYearGenderViolenceDocUrl,
                        )}
                      />
                      {errors?.genderBasedViolencePolicy?.documentUrl ? (
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "14px",
                            minHeight: "20px",
                          }}
                        >
                          {errors?.genderBasedViolencePolicy?.documentUrl}
                        </FormHelperText>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        width: "30%",
                        mt: hasUploadedDocument(
                          getDisplayedPolicyDocument("genderBasedViolencePolicy"),
                        )
                          ? "15px"
                          : "10px",
                          ml:hasUploadedDocument(
                            getDisplayedPolicyDocument("genderBasedViolencePolicy"),
                          ) ? "0.8rem" : "1rem",
                      }}
                    >
                      <TextField
                        fullWidth
                        name="remark"
                        value={formData.genderBasedViolencePolicy.remark || ""}
                        onChange={(e) =>
                          handlePolicyChange(e, "genderBasedViolencePolicy")
                        }
                        variant="outlined"
                        size="small"
                        placeholder="Enter Additional Text"
                        multiline
                        minRows={1}
                        error={!!errors?.genderBasedViolencePolicy?.remark}
                        InputProps={{ sx: { padding: "9px 12px" } }}
                      />
                      <FormHelperText
                        sx={{
                          color: "#D34040",
                          fontSize: "14px",
                          minHeight: "20px",
                        }}
                      >
                        {errors?.genderBasedViolencePolicy?.remark || " "}
                      </FormHelperText>
                    </Box>
                  </Box>
                  <Typography
                    component="p"
                    className={styles.form_heading}
                    sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                  >
                    Effective date (for Policy):
                  </Typography>
                  <TextField
                    type="date"
                    name="effectiveDate"
                    value={
                      formData.genderBasedViolencePolicy.effectiveDate
                        ? String(
                            formData.genderBasedViolencePolicy.effectiveDate,
                          ).split("T")[0]
                        : ""
                    }
                    inputProps={{
                      min: "2021-01-01",
                      ...(diversityPolicyEffMax
                        ? { max: diversityPolicyEffMax }
                        : { max: maxEffectiveDate }),
                    }}
                    onChange={(e) =>
                      handlePolicyChange(e, "genderBasedViolencePolicy")
                    }
                    size="small"
                    error={!!errors?.genderBasedViolencePolicy?.effectiveDate}
                    sx={{
                      width: "30%",
                      ml: "1.1rem",
                      mb: "0.25rem",
                      "& .MuiInputBase-root": { height: 40 },
                    }}
                  />
                  <FormHelperText
                    sx={{
                      color: "#D34040",
                      fontSize: "14px",
                      marginLeft: "1.1rem",
                      width: "30%",
                    }}
                  >
                    {errors?.genderBasedViolencePolicy?.effectiveDate}
                  </FormHelperText>
                </Box>
              </>
            ) : (
              <div style={{ padding: "0 1rem", marginTop: "0.5rem" }}>
                {/* <p
                  className={styles.form_heading}
                  style={{ fontWeight: "bold", marginTop: "1rem" }}
                >
                  Remark
                </p> */}
                <TextField
                  name="statusremark"
                  style={{ width: "30%", marginTop: "7px" }}
                  value={formData.genderBasedViolencePolicy.statusremark || ""}
                  onChange={(e) =>
                    handlePolicyChange(e, "genderBasedViolencePolicy")
                  }
                  variant="outlined"
                  size="small"
                  placeholder="Enter Additional Text"
                  error={!!errors?.genderBasedViolencePolicy?.statusremark}
                  helperText={errors?.genderBasedViolencePolicy?.statusremark}
                  FormHelperTextProps={{
                    sx: {
                      marginLeft: 0,
                      paddingLeft: "1px",
                    },
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Box
        sx={{
          width: "100%",
          mt: "10%",
          mb: 5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outlined"
          sx={backButtonSx}
          onClick={() => navigate(`/social/data/employee-wellbeing/${id}`)}
        >
          <WestIcon sx={{ mr: 2 }} /> Back
        </Button>
        <Button
          variant="contained"
          className={styles.submit_button}
          onClick={handleSubmit}
          sx={primaryButtonSx}
        >
          Save & Continue <ArrowForwardIcon sx={{ ml: 1 }} />
        </Button>
      </Box>
    </MainDashboardLayout>
  );
};

export default Diversity;
